import { SupportedLanguage } from '@/hooks/useLanguage';

// Simple fallback holiday checks without external dependencies
const isSimpleHoliday = (date: Date): boolean => {
  const month = date.getMonth() + 1;
  const day = date.getDate();
  
  // New Year's Day
  if (month === 1 && day === 1) return true;
  
  // Christmas holidays  
  if (month === 12 && (day === 24 || day === 25 || day === 26)) return true;
  
  // German Unity Day (for German locale)
  if (month === 10 && day === 3) return true;
  
  // May Day
  if (month === 5 && day === 1) return true;
  
  return false;
};

// Map language codes to country codes for holiday calculation
export const getCountryCodeFromLanguage = (language: SupportedLanguage): string => {
  const languageToCountryMap: { [key in SupportedLanguage]: string } = {
    de: 'DE', // Germany
    en: 'GB', // United Kingdom
    fr: 'FR', // France
    es: 'ES', // Spain
    it: 'IT', // Italy
    nl: 'NL', // Netherlands
    pl: 'PL', // Poland
    cs: 'CZ', // Czech Republic
    sk: 'SK', // Slovakia
  };

  return languageToCountryMap[language] || 'DE'; // Default to Germany
};

// Check if a date is a holiday for a specific country
export const isHolidayForCountry = (date: Date, countryCode: string): boolean => {
  // Use simple holiday check for now
  return isSimpleHoliday(date);
};

// Get the next available working day (not weekend or holiday)
export const getNextAvailableWorkingDay = (date: Date, countryCode: string): Date => {
  let nextDay = new Date(date);
  let attempts = 0;
  const maxAttempts = 30; // Prevent infinite loops

  while (attempts < maxAttempts) {
    const dayOfWeek = nextDay.getDay();
    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6; // Sunday or Saturday
    const isHoliday = isSimpleHoliday(nextDay);

    if (!isWeekend && !isHoliday) {
      break;
    }

    nextDay.setDate(nextDay.getDate() + 1);
    attempts++;
  }

  // If we couldn't find a working day after many attempts, just return the original date + 1
  if (attempts >= maxAttempts) {
    console.warn('Could not find a working day after many attempts, returning next day');
    return new Date(date.getTime() + 24 * 60 * 60 * 1000);
  }

  return nextDay;
};

// Check if a date is a weekend
export const isWeekend = (date: Date): boolean => {
  const dayOfWeek = date.getDay();
  return dayOfWeek === 0 || dayOfWeek === 6; // Sunday or Saturday
};